import { Button, Chip, Typography, makeStyles } from '@material-ui/core'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import type { FindingCategory } from 'api/graphQL/types/specIntelligence.types'
import AIIcon from 'resources/AIIcon'
import React from 'react'
import { CATEGORY_COLORS, CATEGORY_DISPLAY_LABELS, SPEC_INT_GRADIENT } from './specIntelligenceConstants'

const WILL_CHECK_CATEGORIES: FindingCategory[] = ['CrossReferences', 'ErrorsAndOmissions', 'Contradictions', 'Coordination']

const useStyles = makeStyles(() => ({
    footer: {
        borderTop: '1px solid #E0E0E0',
        padding: `${pxToRem(10)} ${pxToRem(12)}`,
        backgroundColor: '#FAFAFA',
        flexShrink: 0,
    },
    willCheckRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: pxToRem(8),
        marginBottom: pxToRem(10),
    },
    willCheckLabel: {
        fontSize: pxToRem(10),
        fontWeight: 600,
        color: '#6D6D6D',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        flexShrink: 0,
    },
    pills: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: pxToRem(4),
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    pill: {
        height: pxToRem(22),
        fontSize: pxToRem(11),
        '& .MuiChip-label': { padding: `0 ${pxToRem(8)}` },
    },
    runButton: {
        width: '100%',
        textTransform: 'none' as const,
        fontWeight: 500,
        fontSize: pxToRem(13),
        '&.Mui-disabled': {
            backgroundColor: '#E0E0E0',
            color: '#9E9E9E',
        },
        '&:not(.Mui-disabled)': {
            background: SPEC_INT_GRADIENT,
            color: '#FFFFFF',
        },
    },
    runButtonIcon: {
        width: pxToRem(16),
        height: pxToRem(16),
        marginRight: pxToRem(6),
    },
}))

/** Props for {@link SpecIntelligenceAnalysisFooter}. */
export interface SpecIntelligenceAnalysisFooterProps {
    /** Number of currently selected sections; drives button label and disabled state. */
    selectedCount: number
    /** Called when the user clicks Run Analysis. */
    onRunAnalysis: () => void
}

/**
 * Footer for the Spec Intelligence drawer — displays the "will check for" category
 * pills and the Run Analysis button.
 */
const SpecIntelligenceAnalysisFooter = ({ selectedCount, onRunAnalysis }: SpecIntelligenceAnalysisFooterProps) => {
    const classes = useStyles()
    return (
        <div className={classes.footer}>
            <div className={classes.willCheckRow}>
                <Typography
                    id="spec-intel-will-check-label"
                    component="h3"
                    className={classes.willCheckLabel}
                >
                    Will check for
                </Typography>
                <ul
                    className={classes.pills}
                    aria-labelledby="spec-intel-will-check-label"
                >
                    {WILL_CHECK_CATEGORIES.map((category) => (
                        <li key={category}>
                            <Chip
                                label={CATEGORY_DISPLAY_LABELS[category]}
                                size="small"
                                className={classes.pill}
                                style={CATEGORY_COLORS[category]}
                            />
                        </li>
                    ))}
                </ul>
            </div>
            <Button
                disabled={selectedCount === 0}
                fullWidth
                onClick={onRunAnalysis}
                className={classes.runButton}
                startIcon={
                    <AIIcon className={classes.runButtonIcon} />
                }
            >
                Run Analysis ({selectedCount} section{selectedCount !== 1 ? 's' : ''})
            </Button>
        </div>
    )
}

export default SpecIntelligenceAnalysisFooter
